// Painel/Card e Seção (DESIGN §5.5). Branco sobre parede com filete (shadow-anel), sem sombra, sem eyebrow.
// Itens do mesmo tipo: UM painel com linhas (divide-y), nunca N cards iguais.
import type { ElementType, HTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cn } from "../lib/cn";
import { Icones } from "./icones";

export interface PropsPainel extends HTMLAttributes<HTMLElement> {
  titulo?: ReactNode;
  /** Ação à direita do título (use <Botao variante="texto">). */
  acao?: ReactNode;
  /** Sem padding interno (listas com LinhaLista cuidam do próprio espaçamento). */
  semPadding?: boolean;
  como?: ElementType;
}

export function Painel({
  titulo,
  acao,
  semPadding,
  como: Tag = "section",
  className,
  children,
  ...resto
}: PropsPainel) {
  return (
    <Tag className={cn("rounded-card bg-painel shadow-anel", !semPadding && "p-4", className)} {...resto}>
      {(titulo || acao) && (
        <div
          className={cn("flex items-baseline justify-between gap-3", semPadding ? "px-4 pt-4 pb-2" : "mb-3")}
        >
          {titulo && <h2 className="text-h2 text-tinta">{titulo}</h2>}
          {acao}
        </div>
      )}
      {children}
    </Tag>
  );
}

/** Painel como linha tocável ("Ranking do mês · 5º de 42  >"). */
export function PainelLink({
  titulo,
  subtitulo,
  valor,
  className,
  ...resto
}: { titulo: ReactNode; subtitulo?: ReactNode; valor?: ReactNode } & LinkProps) {
  return (
    <Link
      className={cn(
        "flex min-h-16 items-center justify-between gap-3 rounded-card bg-painel px-4 py-3 shadow-anel hover:bg-recuo",
        className,
      )}
      {...resto}
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-titulo text-tinta">{titulo}</span>
        {subtitulo && <span className="truncate text-apoio text-texto-2">{subtitulo}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {valor && <span className="text-titulo placar text-tinta">{valor}</span>}
        <Icones.avancar size={20} aria-hidden className="text-texto-3" />
      </span>
    </Link>
  );
}

/** Seção fora de painel: h2 com respiro de 32 acima. */
export function Secao({
  titulo,
  acao,
  className,
  children,
}: {
  titulo: ReactNode;
  acao?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn("mt-8", className)}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-h2 text-tinta">{titulo}</h2>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** Lista dentro de painel: <ul> com divisórias. Filhos devem ser <LinhaLista>. */
export function ListaPainel({
  children,
  className,
  rotulo,
}: {
  children: ReactNode;
  className?: string;
  rotulo?: string;
}) {
  return (
    <ul aria-label={rotulo} className={cn("divide-y divide-fio", className)}>
      {children}
    </ul>
  );
}
